import {
  Avatar,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import LazyLoadOnScroll from "../components/LazyLoadOnScroll";
import ShareLinkModal from "../components/ShareLinkModal";
import { Empty } from "../components/card/Empty";
import { ReadMore } from "../components/card/ReadMore";
import { PrimaryImage } from "../components/product_detail_page/PrimaryImage";
import ProductDetailSkeleton from "../components/product_detail_page/ProductDetailSkeleton";
import { CardSkeleton } from "../components/skeleton/CardSkeleton";
import { SpinnerLoading } from "../components/spinner/SpinnerLoading";
import { ProductDetailLandingInterface } from "../constants/Product";
import { CurrentActiveUserInterface } from "../constants/User";
import { useFavorite } from "../hooks/fetchApi";
import { useGetUserLoginStore } from "../store/useGetUserLoginStore";
import {
  useBgHover,
  useBorderColor,
  useDividerColor,
  useRedSoftPrimary,
  useReverseDarkWhite,
} from "../themes/colors";
import { axiosPublic } from "../utils/AxiosInstance";
import { MetaTag } from "../utils/MetaTag";
import formatNumber, {
  formatToRupiah,
  getVisitorId,
} from "../utils/helperFunction";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { RiApps2AddFill } from "@remixicon/react";

const RecomendationProduct = lazy(
  () => import("../components/product_detail_page/RecomendationProduct")
);

const FooterButton = lazy(
  () => import("../components/product_detail_page/FooterButton")
);

const BoxDetailProductLayout = lazy(
  () => import("../components/product_detail_page/BoxDetailProductLayout")
);

const ShippingCostsBuyer = lazy(
  () => import("../components/product_detail_page/ShippingCostsBuyer")
);

const ProductDetail = () => {
  const toast = useToast();
  const { prefix, slug } = useParams();

  // Get product detail
  const [isGetProductDetailLoading, setIsGetProductDetailLoading] =
    useState<boolean>(false);
  const [productDetail, setProductDetail] = useState<
    ProductDetailLandingInterface | undefined
  >(undefined);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const [currentImage, setCurrentImage] = useState(0);

  // Add to cart state
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<any>({});
  const [subtotal, setSubtotal] = useState<number>(0);
  const { currentActiveUser: user, getReturnActiveUser } =
    useGetUserLoginStore();
  const [isProductFavorite, setIsProductFavorite] = useState<boolean>(false);
  const { isAddingToFavoriteLoading, toggleFavorite } = useFavorite(
    toast,
    isProductFavorite,
    setIsProductFavorite
  );

  const bgHover = useBgHover();
  const redPrimary = useRedSoftPrimary();
  const reverseBnw = useReverseDarkWhite();
  const dividerColor = useDividerColor();
  const borderColor = useBorderColor();

  // Get product detail
  const getProductDetail = useCallback(async () => {
    setIsGetProductDetailLoading(true);
    const parser = new UAParser();
    const deviceType = parser.getDevice().type;

    const url = `${process.env.REACT_APP_WEB_API_BASE_URL}/v1/store/${prefix}/${slug}`;

    try {
      const cookieUser: CurrentActiveUserInterface | null =
        await getReturnActiveUser();
      const visitorId = await getVisitorId();

      const response = await axiosPublic.get(url, {
        headers: {
          "X-Device-Type": deviceType,
          "Buyer-Id": cookieUser?.role === "buyer" ? cookieUser.id : "",
          "Visitor-Id": visitorId?.toString(),
        },
      });

      setProductDetail(response?.data?.data);

      const filteredVariant: any[] = (
        response.data.data.product.variants as []
      ).filter((variantRes: any) => variantRes.stock > 0);

      if (filteredVariant.length > 0) {
        setSelectedVariant(filteredVariant[0]);
      }

      setIsProductFavorite(
        response?.data?.data?.product?.variants[0]?.is_favorite
      );
      setSubtotal(Number(response?.data?.data?.product?.variants[0].price));
    } catch (error: any) {
      if (error.response.data.status === 404) {
        return setIsNotFound(true);
      }

      toast({
        title: error?.response?.data?.message || "Terjadi kesalahan.",
        description: error.toString(),
        status: "error",
        duration: 2000,
      });
    } finally {
      setIsGetProductDetailLoading(false);
    }
  }, [prefix, slug, toast]);

  useEffect(() => {
    setSubtotal(Number(selectedVariant?.price) * quantity);
  }, [selectedVariant, quantity]);

  useEffect(() => {
    getProductDetail();
  }, []);

  // Handle image click
  const goToImage = (index: number) => {
    setCurrentImage(index);
  };

  if (isNotFound) {
    return (
      <Empty
        title="Tidak ditemukan"
        subtitle="Produk yang anda cari tidak ditemukan, cek kembali link produk"
      />
    );
  }

  return (
    <>
      <MetaTag
        title={
          (productDetail?.product?.name &&
            productDetail?.product?.name + " | " + productDetail?.store.name) ||
          "Produk | BisnisLink"
        }
        description={`${productDetail?.product?.description}`}
        image={productDetail?.product?.main_picture}
      />

      {isGetProductDetailLoading ? (
        <ProductDetailSkeleton />
      ) : (
        <Grid
          templateColumns="repeat(14, 1fr)"
          gap={{ base: 0, md: "20px", lg: "32px", xl: "20px", "2xl": "32px" }}
          px={{
            base: "12px",
            sm: "24px",
            md: "32px",
            lg: "40px",
            xl: "48px",
            "2xl": 0,
          }}
          py="24px"
        >
          {/* Product Images (7/14) */}
          <GridItem
            colSpan={{ base: 14, md: 8, lg: 8, xl: 7 }}
            overflowX="hidden"
          >
            <PrimaryImage
              currentImage={currentImage}
              images={productDetail?.product.images as string[]}
            />

            {/* Thumbnail Navigation */}
            <Grid
              mt={{ base: "12px", lg: "24px" }}
              w={"full"}
              templateColumns={`repeat(${productDetail?.product?.images?.length}, minmax(75px, 100px))`}
              gap={"16px"}
              overflowX={"auto"}
            >
              {productDetail?.product?.images &&
                (productDetail.product.images as string[]).map(
                  (image: string, index: number) => (
                    <Box
                      key={index}
                      as="button"
                      onClick={() => goToImage(index)}
                      maxW={"100px"}
                      h={"75px"}
                      overflow={"hidden"}
                      border={
                        currentImage === index ? "2px solid green" : "none"
                      }
                      borderRadius="8px"
                    >
                      <Image
                        src={
                          image
                            ? `${process.env.REACT_APP_MEDIA_URL}${image}`
                            : ""
                        }
                        alt={`Thumbnail ${index + 1}`}
                        objectFit="cover"
                        w={"full"}
                        h={"full"}
                        transition="transform 0.3s"
                        _hover={{
                          transform: "scale(1.1)",
                        }}
                      />
                    </Box>
                  )
                )}
            </Grid>
          </GridItem>

          {/* Product Data (4/14) */}
          <GridItem colSpan={{ base: 14, md: 6, xl: 4 }}>
            <Breadcrumb
              spacing="8px"
              fontSize="xs"
              mt={{ base: "24px", md: 0 }}
              separator={<ChevronRightIcon color="gray.500" />}
            >
              <BreadcrumbItem>
                <BreadcrumbLink href="/product">Produk</BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>
                  {productDetail?.product?.categories}
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem></BreadcrumbItem>
            </Breadcrumb>
            {/* Product Name and Price */}
            <HStack
              alignItems={"center"}
              justify={"between"}
              justifyContent={"space-between"}
            >
              <Text textStyle={"h-title"}>{productDetail?.product?.name}</Text>
              <ShareLinkModal
                display={{ base: "block", xl: "none" }}
                color="primary"
                buttonText=""
                iconSize="30px"
                data={window.location.href}
              />
            </HStack>
            <Text textStyle={"h-title"} color={"red.400"} mt={"8px"}>
              {selectedVariant?.price
                ? formatToRupiah(parseInt(selectedVariant?.price))
                : "Rp 0"}
            </Text>
            {/* variants */}
            <Box mt={"20px"}>
              <Text textStyle={"b-sm-sb"}>Pilih Varian</Text>

              <HStack spacing={3} wrap={"wrap"} mt={3}>
                {productDetail?.product?.variants?.map(
                  (variant: any, index: number) => (
                    <Button
                      key={variant.id}
                      fontSize={[16, null, 18]}
                      size={{ base: "sm", sm: "sm", xl: "sm" }}
                      fontWeight={"normal"}
                      borderRadius={"md"}
                      color={reverseBnw}
                      isDisabled={variant.emptyStock === 1 ? true : false}
                      variant={`${
                        selectedVariant?.id === variant?.id
                          ? "outlined-lg-secondary"
                          : "outlined-md-gray"
                      }`}
                      bg={
                        selectedVariant.id === variant.id
                          ? redPrimary
                          : undefined
                      }
                      borderColor={
                        selectedVariant.id === variant.id
                          ? "red.400"
                          : borderColor
                      }
                      _hover={{
                        bg:
                          selectedVariant.id === variant.id
                            ? redPrimary
                            : bgHover,
                      }}
                      onClick={() => {
                        setSelectedVariant(variant);
                        setQuantity(1);
                        setSubtotal(Number(variant?.price));
                        productDetail.product?.variants &&
                          setIsProductFavorite(
                            //perlu dikoreksi lagi
                            productDetail.product.variants[index]
                              .is_favorite === 1
                          );
                      }}
                    >
                      <Text fontSize={{ base: "12px", lg: "14px" }}>
                        {variant?.name}
                      </Text>
                    </Button>
                  )
                )}
              </HStack>
            </Box>
            {/* Product Details */}
            <Box
              mt={"20px"}
              borderBottomWidth={"1px"}
              borderBottomColor={dividerColor}
              pb={"20px"}
            >
              <Text textStyle={"b-sm-sb"}>Detail Produk</Text>

              <Text as={"p"} mt={"8px"}>
                <Text as={"span"} textStyle={"b-reg"} variant={"secondary"}>
                  Minimal Pemesanan :
                </Text>

                <Text as={"span"} textStyle={"b-reg"} ml={"2px"}>
                  1 Buah
                </Text>
              </Text>

              <Text as={"p"} mt={"2px"}>
                <Text as={"span"} textStyle={"b-reg"} variant={"secondary"}>
                  Stok :
                </Text>

                <Text as={"span"} textStyle={"b-reg"} ml={"2px"}>
                  {selectedVariant?.stock > 0
                    ? formatNumber(selectedVariant?.stock)
                    : 0}
                </Text>
              </Text>

              <Text as={"p"} mt={"2px"}>
                <Text as={"span"} textStyle={"b-reg"} variant={"secondary"}>
                  Kategori :
                </Text>

                <Text as={"span"} textStyle={"b-reg"} ml={"2px"}>
                  {productDetail?.product?.categories}
                </Text>
              </Text>

              {/* Description */}
              <ReadMore data={productDetail?.product.description} mt={4} />
            </Box>
             {/* Reseller profile */}
            <HStack
              as={Link}
              to={`/reseller/${productDetail?.store?.prefix}`}
              spacing={2}
              align={"center"}
              borderBottomWidth={"1px"}
              borderBottomColor={dividerColor}
              pb={"30px"}
            >
              <Avatar
                src={
                  productDetail?.store.logo
                    ? `${process.env.REACT_APP_MEDIA_URL}${productDetail?.store?.logo}`
                    : ""
                }
                name={productDetail?.store?.name || ""}
                size={"md"}
              />

              <Text textStyle={"h-subtitle"} fontWeight={"semibold"}>
                {productDetail?.store?.name}
              </Text>
            </HStack>
            {/* Button for reseller role */}
            {user?.role === "reseller" && (
              <Button
                as={Link}
                to={`/reseller/search/product/${productDetail?.product?.id}`}
                leftIcon={<Icon as={RiApps2AddFill} />}
                w={"full"}
                size={"sm"}
                colorScheme="red"
              >
                Kelola Produk
              </Button>
            )}
            {/* Delivery */}
            {user?.role === "buyer" && (
              <LazyLoadOnScroll>
                <Suspense
                  fallback={<SpinnerLoading title="Mencari ongkir termurah" />}
                >
                  <ShippingCostsBuyer
                    currentActiveUser={user}
                    prefix={prefix}
                    slug={slug}
                  />
                </Suspense>
              </LazyLoadOnScroll>
            )}
          </GridItem>

          {/* Action to Buy */}
          {(!user || user.role === "buyer") && (
            <Suspense fallback={<CardSkeleton row={1} height={400} />}>
              <BoxDetailProductLayout
                selectedVariant={selectedVariant}
                productDetail={productDetail}
                quantity={quantity}
                isProductFavorite={isProductFavorite}
                setIsProductFavorite={setIsProductFavorite}
                subTotal={subtotal}
                onSetQuantity={(type: "add" | "subtract") => {
                  if (type === "add" && selectedVariant?.stock > quantity) {
                    setQuantity(quantity + 1);
                  } else if (type === "subtract" && quantity > 1) {
                    setQuantity(quantity - 1);
                  }
                }}
              />
            </Suspense>
          )}
        </Grid>
      )}

      {/* Products Recommended */}
      {productDetail?.store.more_products &&
        productDetail.store.more_products.length > 0 && (
          <LazyLoadOnScroll>
            <Suspense fallback={<CardSkeleton row={4} height={300} />}>
              <RecomendationProduct
                isLoading={isGetProductDetailLoading}
                data={productDetail}
                resellerPrefix={prefix}
              />
            </Suspense>
          </LazyLoadOnScroll>
        )}

      {(!user || user.role === "buyer") && (
        <Suspense fallback={<CardSkeleton row={1} height={60} />}>
          <FooterButton
            isProductFavorite={isProductFavorite}
            setIsProductFavorite={setIsProductFavorite}
            productDetail={productDetail}
            loaded={isGetProductDetailLoading}
            selectedVariant={selectedVariant}
          />
        </Suspense>
      )}
    </>
  );
};

export default ProductDetail;
